import { type OrderEmail, useOrderEmails } from '@/api/hooks/useOrderEmails'
import { formatTimestamp } from '@/utils/dateFormatters'
import { CloseOutlined, MailOutlined } from '@ant-design/icons'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Drawer, Empty, Spin, Tag, theme } from 'antd'

interface OrderEmailsDrawerProps {
  orderId: string | null | undefined
  open: boolean
  onClose: () => void
}

const statusColor: Record<OrderEmail['status'], string> = {
  sent: 'success',
  failed: 'error',
  pending: 'default',
}

function EmailCard({ email }: { email: OrderEmail }) {
  const { token } = theme.useToken()
  const recipients = email.actual_recipients.length
    ? email.actual_recipients
    : email.intended_recipients

  return (
    <Vertical
      gap={6}
      style={{
        flex: '0 0 auto',
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadius,
        padding: 12,
        background: token.colorBgContainer,
      }}
    >
      <Horizontal alignItems='center' justifyContent='space-between' gap={8}>
        <Horizontal alignItems='center' gap={8} style={{ minWidth: 0 }}>
          <MailOutlined style={{ color: token.colorTextSecondary }} />
          <Texto style={{ fontWeight: 600 }}>{email.subject}</Texto>
        </Horizontal>
        <Tag color={statusColor[email.status]} style={{ marginInlineEnd: 0 }}>
          {email.status}
        </Tag>
      </Horizontal>

      <Texto style={{ fontSize: 13, color: token.colorTextSecondary }}>
        To: {recipients.length ? recipients.join(', ') : '—'}
      </Texto>

      <Texto style={{ fontSize: 12, color: token.colorTextTertiary }}>
        {email.sent_at ? formatTimestamp(email.sent_at) : 'Not sent'}
      </Texto>
    </Vertical>
  )
}

export function OrderEmailsDrawer({
  orderId,
  open,
  onClose,
}: OrderEmailsDrawerProps) {
  const { token } = theme.useToken()
  const { data: emails, isLoading } = useOrderEmails(orderId)

  // Newest first by sent time, falling back to keep unsent entries grouped.
  const sortedEmails = [...(emails ?? [])].sort((a, b) => {
    const aTime = a.sent_at ? new Date(a.sent_at).getTime() : 0
    const bTime = b.sent_at ? new Date(b.sent_at).getTime() : 0
    return bTime - aTime
  })

  return (
    <Drawer
      title='Order Emails'
      placement='right'
      width={560}
      open={open}
      onClose={onClose}
      closeIcon={<CloseOutlined />}
      styles={{
        body: { padding: 16 },
      }}
    >
      {isLoading ? (
        <Vertical
          className='h-full'
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
          }}
        >
          <Spin />
          <span style={{ marginTop: 12, color: token.colorTextSecondary }}>
            Loading emails...
          </span>
        </Vertical>
      ) : sortedEmails.length === 0 ? (
        <Empty description='No emails sent for this order' />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sortedEmails.map((email, idx) => (
            <EmailCard key={email._id ?? idx} email={email} />
          ))}
        </div>
      )}
    </Drawer>
  )
}
